import { translateMetadata } from '@/utils';
import { guards, type ILearningObjectResourceResponse, type IncludedType, type LoResourceType } from '@/services';
import { type IModuleData, type IALMResourceData, ModuleContentTypes, ALMResourceContentTypes } from '@/entities';
import type { IResourceResponse } from '@/services/prime/resource';
import { mapPrimeLearningObjectNote } from '@/mappers/note';

export const mapPrimeModule = (data: ILearningObjectResourceResponse, included?: IncludedType[]): IModuleData => {
  const learningObjectResourceGrade = included
    ?.filter(guards.isGrade)
    .find((grade) => grade.relationships.loResource?.data.id === data.id);
  const learningObjectNotes = included
    ?.filter(guards.isNote)
    .filter((n) => n.relationships?.loResource?.data.id === data.id);
  const resourceIds = (data.relationships?.resources?.data || []).map((item) => item.id);
  const resources = included?.filter(guards.isResourceData).filter((resource) => resourceIds.includes(resource.id));

  return {
    id: data.id,
    courseId: data.relationships?.learningObject?.data.id || '',
    instanceId: data.relationships?.loInstance?.data.id || '',
    name: translateMetadata(data.attributes?.localizedMetadata)?.name || '',
    description: translateMetadata(data.attributes?.localizedMetadata)?.description || '',
    contentType: toModuleContentType(data.attributes?.loResourceType),
    moduleType: data.attributes.resourceType || 'Elearning',
    hasPassed: learningObjectResourceGrade?.attributes.hasPassed || false,
    progress: learningObjectResourceGrade?.attributes.progressPercent || 0,
    durationInMinutes: learningObjectResourceGrade?.attributes.duration || 0,
    notes: learningObjectNotes?.map((n) => mapPrimeLearningObjectNote(n)) || [],
    mandatory: data.attributes?.mandatory || false,
    isSubmissible: data.attributes.resourceSubType == 'SUBMISSION' && data.attributes.resourceType == 'Activity',
    resources: resources?.map((item) => toResources(item))
  };
};

const toResources = (resource: IResourceResponse): IALMResourceData => {
  return {
    id: resource.id,
    name: resource.attributes?.name || '',
    contentType: toContentType(resource.attributes.contentType),
    location: resource.attributes.location,
    seatLimit: resource.attributes.seatLimit,
    instructorNames: resource.attributes.instructorNames,
    dateStart: resource.attributes.dateStart,
    completionDeadline: resource.attributes.completionDeadline,
    duration: resource.attributes.desiredDuration,
    desiredDuration: resource.attributes.desiredDuration
  };
};

const toModuleContentType = (loResourceType: LoResourceType | undefined): ModuleContentTypes => {
  if (loResourceType == 'Test Out') return ModuleContentTypes.TestOut;
  if (loResourceType == 'Pre Work') return ModuleContentTypes.PreWork;
  return ModuleContentTypes.Content;
};

const toContentType = (contentType: ALMResourceContentTypes): ALMResourceContentTypes => {
  if (contentType == 'Activity') return ALMResourceContentTypes.Activity;
  if (contentType == 'Classroom') return ALMResourceContentTypes.Classroom;
  if (contentType == 'Virtual Classroom') return ALMResourceContentTypes.VirtualClassroom;
  else return ALMResourceContentTypes.Other;
};
